import { UserHeadCell, UserRole } from './types'

/* Layout */
export const SIDEBAR_MENU_WIDTH = 68
export const SIDE_DRAWER_WIDTH = 20
export const TOP_NAVBAR_HEIGHT = 70

/* Mapbox */
export const BASE_MAP_LINZ =
  'mapbox://styles/urbanintelligence/cljgef8p2002t01r7fr970avz?optimize=true'
export const BASE_MAP_LIGHT_WITH_HOUSE_NUMBERS =
  'mapbox://styles/urbanintelligence/cm3cgoyko002o01rg46ofhud7?optimize=true'
export const BASE_MAP_CONTOURS = 'mapbox://styles/mapbox/outdoors-v12?optimize=true'

export const REGION_ZOOM_LEVEL = 11
export const NEW_ZEALAND_ZOOM_LEVEL = 5.7
export const NEW_ZEALAND_LAT = -41.1220914
export const NEW_ZEALAND_LGT = 171.6418355

export const APP_INITIAL_LOCATION = {
  latitude: NEW_ZEALAND_LAT,
  longitude: NEW_ZEALAND_LGT,
  zoom: NEW_ZEALAND_ZOOM_LEVEL,
}

/* API */
export const BASE_API_URL = import.meta.env.VITE_LOCAL_BASE_URL
  ? import.meta.env.VITE_LOCAL_BASE_URL
  : ''

/* Kotare */
export const BASE_KOTARE_URL = import.meta.env.VITE_LOCAL_KOTARE_URL
  ? import.meta.env.VITE_LOCAL_KOTARE_URL
  : ''

/* Validation */
export const validEmailRegex = new RegExp(
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
)

export const PUBLIC_ASSETS_BASE_URL = import.meta.env.VITE_PUBLIC_ASSETS_BASE_URL
  ? import.meta.env.VITE_PUBLIC_ASSETS_BASE_URL
  : 'https://d2ufl17mx4sema.cloudfront.net/public'

export const FEATURE_FLAGS = {
  REPLACEMENT_COSTS: 'replacementCosts',
  ATTRIBUTE_FILTERS: 'attributeFilters',
  ONBOARDING_PAGE: 'onboardingPage',
  PROPERTY_PAGE: 'propertyPage',
  PRIORITISATION_PAGE: 'prioritisationPage',
  NEW_LANDING_PAGE: 'newLandingPage',
  LIVE_PAGE: 'livePage',
  NEW_HAZARD_CONTROLS: 'newHazardControls',
  HIDE_ACCESS_MODULE: 'hideAccessModule',
  RISK_MODULE: 'riskModule',
  POPULATION: 'population',
  RISKTABLE: 'riskTable',
  DATA_SUBMODULE: 'dataSubmodule',
  ADAPTATION_LIBRARY: 'adaptationLibrary',
  FORUM_LINK: 'forumLink',
  SECOND_TSNCS_CHECKBOX: 'secondTsncsCheckbox',
  RISK_STORIES: 'riskStories',
  // Backend=only flag to trigger emails on report generation
  EMAIL_REPORT_TO_USER: 'emailReportToUser',
  CUSTOM_USER_ROLES: 'customUserRoles',
  HAZARD_REFERENCE: 'hazardReference',
  LAYER_PERMISSIONS: 'layerPermissions',
  DATA_UPLOAD: 'dataUpload',
  SHADCN_UI: 'shadcnUi',
  REX_LIVE: 'rexLive',
  DAMAGE_COST_REPORTING: 'damageCostReporting',
  RISK_PROFILE: 'riskProfile',
  ADAPTATION_CYCLE: 'adaptationCycle',
  PROFILE_ECONOMIC_CONTENT: 'profileEconomicContent',
  PROFILE_SOCIAL_CONTENT: 'profileSocialContent',
  WORKSPACE_V2: 'workspacesV2',
  EVIDENCE_LIBRARY: 'evidenceLibraryDocuments',
  RISK_PROFILE_T3: 'riskProfileT3',
} as const

// Feature flags that can be managed by admin users (not just staff)
export const MODULE_FLAGS = [
  FEATURE_FLAGS.RISK_MODULE,
  FEATURE_FLAGS.PROPERTY_PAGE,
  FEATURE_FLAGS.REX_LIVE,
] as const

// Descriptions for module flags shown in the admin interface
export const MODULE_FLAGS_DESCRIPTIONS: Record<string, string> = {
  [FEATURE_FLAGS.RISK_MODULE]:
    'Access to the Risk module for exploring climate and environmental risks',
  [FEATURE_FLAGS.PROPERTY_PAGE]:
    'Access to the Property module for property-specific reports and insights',
  [FEATURE_FLAGS.REX_LIVE]: 'Access to the Live module for real-time monitoring and alerts',
}

export const FEATURE_FLAG_LABELS: Record<string, string> = {
  [FEATURE_FLAGS.REPLACEMENT_COSTS]: 'Replacement Costs',
  [FEATURE_FLAGS.ATTRIBUTE_FILTERS]: 'Attribute Filters',
  [FEATURE_FLAGS.ONBOARDING_PAGE]: 'Onboarding Page',
  [FEATURE_FLAGS.PROPERTY_PAGE]: 'Property Module',
  [FEATURE_FLAGS.PRIORITISATION_PAGE]: 'Prioritisation Page',
  [FEATURE_FLAGS.NEW_LANDING_PAGE]: 'New Landing Page',
  [FEATURE_FLAGS.LIVE_PAGE]: 'Live Page',
  [FEATURE_FLAGS.NEW_HAZARD_CONTROLS]: 'New Hazard Controls',
  [FEATURE_FLAGS.HIDE_ACCESS_MODULE]: 'Hide Access Module',
  [FEATURE_FLAGS.RISK_MODULE]: 'Risk Module',
  [FEATURE_FLAGS.POPULATION]: 'Population',
  [FEATURE_FLAGS.FORUM_LINK]: 'Forum Link',
  [FEATURE_FLAGS.ADAPTATION_LIBRARY]: 'Adaptation Library',
  [FEATURE_FLAGS.DATA_SUBMODULE]: 'Data Submodule',
  [FEATURE_FLAGS.RISKTABLE]: 'Risk Table',
  [FEATURE_FLAGS.RISK_STORIES]: 'Risk Stories',
  [FEATURE_FLAGS.SECOND_TSNCS_CHECKBOX]: 'Second TSNCS Checkbox',
  [FEATURE_FLAGS.EMAIL_REPORT_TO_USER]: 'Email Report to User',
  [FEATURE_FLAGS.CUSTOM_USER_ROLES]: 'Custom User Roles',
  [FEATURE_FLAGS.LAYER_PERMISSIONS]: 'Layer Permissions',
  [FEATURE_FLAGS.DATA_UPLOAD]: 'Data Upload',
  [FEATURE_FLAGS.SHADCN_UI]: 'ShadCN UI',
  [FEATURE_FLAGS.REX_LIVE]: 'Live Module',
  [FEATURE_FLAGS.DAMAGE_COST_REPORTING]: 'Damage Cost Reporting',
  [FEATURE_FLAGS.RISK_PROFILE]: 'Risk Profile',
  [FEATURE_FLAGS.ADAPTATION_CYCLE]: 'Adaptation Cycle',
  [FEATURE_FLAGS.PROFILE_ECONOMIC_CONTENT]: 'Profile Economic Content',
  [FEATURE_FLAGS.PROFILE_SOCIAL_CONTENT]: 'Profile Social Content',
  [FEATURE_FLAGS.WORKSPACE_V2]: 'Workspaces V2',
  [FEATURE_FLAGS.EVIDENCE_LIBRARY]: 'Evidence Library (Documents)',
  [FEATURE_FLAGS.RISK_PROFILE_T3]: 'Risk Profile T3',
}

export const ROLE_COLORS: Record<
  Exclude<UserRole, typeof UserRole.STAFF>,
  'secondary' | 'info' | 'success' | 'warning'
> = {
  admin: 'secondary',
  member: 'info',
  viewer: 'success',
  public: 'warning',
}

export const USER_ROLES_LABELS_MAP = {
  [UserRole.VIEWER]: 'Can view results',
  [UserRole.MEMBER]: 'Can view and export results',
  [UserRole.ADMIN]: 'Can manage users and access',
}

export const SELECTABLE_ROLES = [UserRole.VIEWER, UserRole.MEMBER, UserRole.ADMIN]

export const manageUsersHeadCells: readonly UserHeadCell[] = [
  {
    id: 'email',
    numeric: false,
    label: 'Email',
    sortable: true,
    align: 'left',
  },
  {
    id: 'date_created',
    numeric: false,
    label: 'Signed Up',
    sortable: true,
    align: 'right',
  },
  {
    id: 'logged_in_count',
    numeric: false,
    label: 'Login Count',
    sortable: true,
    align: 'right',
  },
  {
    id: 'last_logged_in',
    numeric: true,
    label: 'Last Login',
    sortable: true,
    align: 'right',
  },
  {
    id: 'status',
    numeric: false,
    label: 'Status',
    sortable: true,
    align: 'center',
  },
  {
    id: 'user_roles',
    numeric: false,
    label: 'Roles',
    sortable: false,
    align: 'center',
  },
  {
    id: 'delete_user',
    numeric: false,
    label: 'Delete User',
    sortable: false,
    align: 'center',
  },
  {
    id: 'edit_user',
    numeric: false,
    label: 'Edit User',
    sortable: false,
    align: 'center',
  },
  {
    id: 'resend_invite',
    numeric: false,
    label: 'Resend Invite',
    sortable: false,
    align: 'center',
  },
]

export const DATADOG_ACTIONS = {
  MAPBOX_MAP_LOADED: 'mapbox-map-loaded',
  EXPORT_REPORT: 'export-report',
  WORKSPACE_CREATED: 'workspace-created',
  WORKSPACE_LOADED: 'workspace-loaded',
  SEND_INVITE: 'send-invite',
  ADD_LAYER: 'add-layer',
  SELECT_FEATURE: 'select-feature',
  MODIFY_HAZARD_SCENARIO: 'modify-hazard-scenario',
  DOCUMENT_UPLOADED: 'document-uploaded',
  DOCUMENT_VIEWED: 'document-viewed',
  DOCUMENT_DOWNLOADED: 'document-downloaded',
  NOTIFICATIONS_VIEWED: 'notifications-viewed',
} as const

export const ERROR_403_MESSAGE = 'Request failed with status code 403'
